import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, ArrowUpRight } from "lucide-react";
import { MaskedLine, EASE } from "./Reveal";

const HERO_IMG =
  "https://images.unsplash.com/photo-1768948166165-b3b332618a08?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzF8MHwxfHNlYXJjaHwyfHxjaW5lbWF0aWMlMjBjb25zdHJ1Y3Rpb24lMjBzaXRlJTIwZGFya3xlbnwwfHx8fDE3ODc1ODI2MzB8MA&ixlib=rb-4.1.0&q=85";

const STATS = [
  { value: "15+", label: "Years North-Tough" },
  { value: "300+", label: "Projects Delivered" },
  { value: "24/7", label: "Winter Dispatch" },
];

export const Hero = ({ onNavigate }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "28%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1.05, 1.28]);
  const fade = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section id="home" ref={ref} className="relative min-h-screen flex flex-col overflow-hidden" data-testid="hero-section">
      <motion.div style={{ y, scale }} className="absolute inset-0 will-change-transform">
        <img src={HERO_IMG} alt="Cinematic construction site at night" className="h-full w-full object-cover" />
      </motion.div>
      <div className="absolute inset-0 bg-black/60" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-transparent to-[#050505]/40" />

      <motion.div style={{ opacity: fade }} className="relative z-10 flex-1 flex flex-col justify-end max-w-[1800px] w-full mx-auto px-6 sm:px-12 lg:px-24 pt-32 pb-10">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 0.3 }}
          className="flex items-center gap-4 mb-6"
          data-testid="hero-meta"
        >
          <span className="h-2 w-2 bg-[#FF3B00] animate-pulse" />
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-[#FF3B00]">
            NorthEdge Contracting — Est. 2009
          </p>
        </motion.div>

        <h1
          className="font-oswald font-bold uppercase leading-[0.85] tracking-tighter text-[17vw] sm:text-[14vw] lg:text-[11vw]"
          data-testid="hero-heading"
        >
          <MaskedLine onLoad delay={0.15}>Building</MaskedLine>
          <MaskedLine onLoad delay={0.3} className="text-outline">Beyond</MaskedLine>
          <MaskedLine onLoad delay={0.45}>Boundaries<span className="text-[#FF3B00]">.</span></MaskedLine>
        </h1>

        <div className="mt-10 grid grid-cols-1 md:grid-cols-12 gap-8 items-end">
          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.9, ease: EASE }}
            className="md:col-span-5 text-base sm:text-lg font-light leading-relaxed text-[#B0B0B0]"
            data-testid="hero-subtext"
          >
            General construction, commercial contracting, home renovation, concrete and snow
            removal — one crew that builds for the thirty winters after the final inspection.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.05, ease: EASE }}
            className="md:col-span-7 flex flex-wrap gap-4 md:justify-end"
          >
            <button
              onClick={() => onNavigate("#contact")}
              className="group relative overflow-hidden bg-[#FF3B00] px-10 py-5 font-mono text-xs uppercase tracking-[0.25em] text-[#050505]"
              data-testid="hero-cta-start-project"
            >
              <span className="absolute inset-0 -translate-y-full bg-[#F2F2F2] transition-transform duration-300 ease-out group-hover:translate-y-0" />
              <span className="relative z-10 flex items-center gap-2 font-bold">
                Start a project <ArrowUpRight className="h-4 w-4" />
              </span>
            </button>
            <button
              onClick={() => onNavigate("#work")}
              className="group relative overflow-hidden border border-[#3A3A3A] px-10 py-5 font-mono text-xs uppercase tracking-[0.25em] text-[#F2F2F2] hover:border-[#FF3B00] transition-colors duration-300"
              data-testid="hero-cta-view-work"
            >
              <span className="relative z-10 flex items-center gap-2 group-hover:text-[#FF3B00] transition-colors duration-300">
                View the work <ArrowDown className="h-4 w-4" />
              </span>
            </button>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1.3 }}
          className="mt-14 pt-6 border-t border-[#F2F2F2]/10 grid grid-cols-3 gap-4"
          data-testid="hero-stats"
        >
          {STATS.map((s) => (
            <div key={s.label} className="flex flex-col gap-1">
              <span className="font-oswald font-bold text-2xl sm:text-4xl text-[#F2F2F2]">{s.value}</span>
              <span className="font-mono text-[10px] sm:text-xs uppercase tracking-[0.2em] text-[#808080]">{s.label}</span>
            </div>
          ))}
        </motion.div>
      </motion.div>
    </section>
  );
};
